import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

export function LoginPage() {
  const { githubOAuthConfigured } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    const hash = window.location.hash;
    if (hash.includes('auth_error=github_not_allowed')) {
      return 'Your GitHub account is not allowed to access this OpenTool instance.';
    }
    return null;
  });

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/auth/github/login');
      const data = await resp.json();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data.error) {
        setError(data.error);
        setLoading(false);
      }
    } catch (e) {
      setError('Failed to connect to server');
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#0d0d0d',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
    }}>
      {/* Subtle grid background for OS feel */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
      }} />

      <div style={{
        background: '#1e1e2e',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 8,
        padding: '48px 40px',
        width: 380,
        maxWidth: '90vw',
        textAlign: 'center',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        {/* Window title bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: 32, background: '#282a36',
          borderRadius: '8px 8px 0 0',
          display: 'flex', alignItems: 'center',
          padding: '0 12px', gap: 8,
        }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5555' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#bd93f9' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#50fa7b' }} />
          <span style={{ fontSize: 10, color: '#6272a4', marginLeft: 8, fontFamily: FONT }}>opentool://login</span>
        </div>

        <div style={{ marginTop: 16 }}>
          {/* Logo */}
          <div style={{
            width: 56,
            height: 56,
            margin: '0 auto 24px',
            borderRadius: 8,
            background: 'rgba(80,250,123,0.08)',
            border: '1px solid rgba(80,250,123,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            fontWeight: 700,
            color: '#50fa7b',
            fontFamily: FONT,
          }}>
            OT
          </div>

          <h1 style={{
            color: '#f8f8f2',
            fontSize: 20,
            fontWeight: 600,
            margin: '0 0 8px',
            fontFamily: FONT,
          }}>
            OpenTool
          </h1>

          <p style={{
            color: '#6272a4',
            fontSize: 11,
            margin: '0 0 32px',
            lineHeight: 1.5,
          }}>
            Universal AI tool control plane
          </p>

          <button
            onClick={handleLogin}
            disabled={loading || !githubOAuthConfigured}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: loading ? '#282a36' : '#282a36',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 4,
              color: '#f8f8f2',
              fontSize: 13,
              fontFamily: FONT,
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              if (!loading) {
                (e.target as HTMLButtonElement).style.borderColor = 'rgba(80,250,123,0.4)';
                (e.target as HTMLButtonElement).style.background = 'rgba(80,250,123,0.06)';
              }
            }}
            onMouseLeave={e => {
              (e.target as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.08)';
              (e.target as HTMLButtonElement).style.background = '#282a36';
            }}
          >
            {/* GitHub icon */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            {loading ? 'Redirecting...' : 'Sign in with GitHub'}
          </button>

          {!githubOAuthConfigured && (
            <p style={{
              color: '#ffb86c',
              fontSize: 11,
              marginTop: 14,
              lineHeight: 1.5,
            }}>
              GitHub OAuth is not configured on this instance yet.
            </p>
          )}

          {error && (
            <p style={{
              color: '#ff5555',
              fontSize: 12,
              marginTop: 16,
              padding: '8px 12px',
              background: 'rgba(255,85,85,0.08)',
              borderRadius: 8,
              border: '1px solid rgba(255,85,85,0.2)',
              fontFamily: FONT,
            }}>
              {error}
            </p>
          )}

          <p style={{
            color: '#6272a4',
            fontSize: 10,
            marginTop: 24,
            lineHeight: 1.6,
          }}>
            First user to sign in becomes the platform admin
          </p>
        </div>
      </div>
    </div>
  );
}
